/**
 * SceneTimelineEditor - visualizes scene actions as a timeline
 * with drag-to-reorder, click-to-edit, and delete support
 *
 * @author    Claude / contexagon GmbH
 */

import React from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
    getActionIcon,
    getActionLabel,
    getActionDescription,
    getActionSubtitle,
    getStartLabel,
    getStartBorderColor,
} from './actionTypes';


function formatDuration(fields) {
    if (!fields) return null;
    const v = fields.duration;
    const s = fields['duration__suffix'] || 's';
    if (v === undefined || v === null || v === '') return null;
    return v + s;
}

function formatDelay(fields) {
    if (!fields) return null;
    const v = fields.delay;
    const s = fields['delay__suffix'] || 's';
    if (!v && v !== 0) return null;
    if (Number(v) === 0) return null;
    return '+' + v + s;
}


function ActionRow({ action, index, isSelected, provided, snapshot, onSelect, onDelete, referencedObjects }) {
    const fields = action.fields || {};
    const start = fields.start || 'initially';
    const borderColor = getStartBorderColor(start);
    const duration = formatDuration(fields);
    const delay = formatDelay(fields);
    const isActive = fields.active !== false;
    const description = getActionDescription(action, referencedObjects);
    const subtitle = getActionSubtitle(action);

    const rowBg = snapshot.isDragging
        ? '#e3f2fd'
        : isSelected
            ? '#f0f7ff'
            : 'white';

    return (
        <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            onClick={onSelect}
            style={{
                ...provided.draggableProps.style,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 3,
                padding: '7px 10px 7px 0',
                borderRadius: 6,
                backgroundColor: rowBg,
                // Use individual longhand properties — avoids React style-diff dropping
                // borderLeft when 'border' shorthand changes (selection toggle bug)
                borderTop: isSelected ? '1px solid #90caf9' : '1px solid #ebebeb',
                borderRight: isSelected ? '1px solid #90caf9' : '1px solid #ebebeb',
                borderBottom: isSelected ? '1px solid #90caf9' : '1px solid #ebebeb',
                borderLeft: '4px solid ' + borderColor,
                cursor: 'pointer',
                opacity: isActive ? 1 : 0.45,
                userSelect: 'none',
                boxShadow: snapshot.isDragging ? '0 4px 16px rgba(0,0,0,0.12)' : 'none',
                transition: 'background-color 0.1s, box-shadow 0.1s',
            }}
        >
            {/* Drag handle */}
            <div
                {...provided.dragHandleProps}
                onClick={e => e.stopPropagation()}
                style={{
                    cursor: 'grab',
                    color: '#d0d0d0',
                    fontSize: 15,
                    flexShrink: 0,
                    padding: '0 6px',
                    alignSelf: 'stretch',
                    display: 'flex',
                    alignItems: 'center',
                }}
                title="Ziehen zum Verschieben"
            >
                ⠿
            </div>

            {/* Row number */}
            <div style={{
                color: '#c0c0c0',
                fontSize: 11,
                width: 18,
                textAlign: 'right',
                flexShrink: 0,
                fontVariantNumeric: 'tabular-nums',
            }}>
                {index + 1}
            </div>

            {/* Type icon */}
            <div style={{ fontSize: 18, flexShrink: 0 }} title={getActionLabel(action.type)}>
                {getActionIcon(action.type)}
            </div>

            {/* Main content: name (primary) + type + timing (secondary) */}
            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                {/* Primary: name / description — this is what staff need to read */}
                <div style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: isSelected ? '#1565c0' : '#1a1a1a',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.3,
                }}>
                    {description || <span style={{ color: '#bbb', fontWeight: 400, fontStyle: 'italic' }}>{getActionLabel(action.type)}</span>}
                </div>

                {/* Secondary: type label + timing + subtitle */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    marginTop: 2,
                    flexWrap: 'nowrap',
                    overflow: 'hidden',
                }}>
                    {/* Type badge */}
                    <span style={{
                        fontSize: 9,
                        fontWeight: 800,
                        color: '#aaa',
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        flexShrink: 0,
                    }}>
                        {getActionLabel(action.type)}
                    </span>

                    {/* Timing badge */}
                    <span style={{
                        fontSize: 9,
                        padding: '1px 5px',
                        borderRadius: 8,
                        backgroundColor: borderColor + '18',
                        color: borderColor,
                        fontWeight: 700,
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                        letterSpacing: 0.2,
                    }}>
                        {getStartLabel(start)}
                    </span>

                    {/* Subtitle (e.g. conditional structure info) */}
                    {subtitle && (
                        <span style={{
                            fontSize: 10,
                            color: '#bbb',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}>
                            {subtitle}
                        </span>
                    )}
                </div>
            </div>

            {/* Time badges */}
            <div style={{ display: 'flex', gap: 3, flexShrink: 0, alignItems: 'center' }}>
                {delay && (
                    <span style={{ fontSize: 10, padding: '2px 5px', borderRadius: 4, backgroundColor: '#f5f5f5', color: '#777' }}>
                        {delay}
                    </span>
                )}
                {duration && (
                    <span style={{ fontSize: 10, padding: '2px 5px', borderRadius: 4, backgroundColor: '#eeeeee', color: '#444', fontWeight: 600 }}>
                        {duration}
                    </span>
                )}
                {!isActive && (
                    <span style={{ fontSize: 10, padding: '2px 5px', borderRadius: 4, backgroundColor: '#ffcdd2', color: '#c62828' }}>
                        inaktiv
                    </span>
                )}
            </div>

            {/* Delete button */}
            <button
                onClick={e => { e.stopPropagation(); onDelete(); }}
                style={{
                    flexShrink: 0,
                    width: 24,
                    height: 24,
                    border: 'none',
                    borderRadius: '50%',
                    backgroundColor: 'transparent',
                    color: '#ccc',
                    cursor: 'pointer',
                    fontSize: 16,
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    marginRight: 4,
                    transition: 'background-color 0.1s, color 0.1s',
                }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#ffebee'; e.currentTarget.style.color = '#e53935'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#ccc'; }}
                title="Aktion löschen"
            >
                ×
            </button>
        </div>
    );
}


class SceneTimelineEditor extends React.Component {

    onDragEnd = (result) => {
        if (!result.destination) return;
        const from = result.source.index;
        const to = result.destination.index;
        if (from !== to) {
            this.props.onReorder(from, to);
        }
    };

    render() {
        const { scene, actions, dirty, saving, selectedActionIndex, referencedObjects = {}, storyMembership = [], onSave, onOpenEditor, onSelectAction, onDeleteAction } = this.props;
        const fields = scene.fields || {};
        const sceneName = fields.name || scene.title || scene.id;
        const category = fields.category || '';
        const targets = Array.isArray(fields.target) ? fields.target : [];

        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Header */}
                <div style={{ marginBottom: 14, flexShrink: 0 }}>
                    {/* Scene name + action buttons */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: category || targets.length > 0 ? 6 : 0 }}>
                        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, flex: 1, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {sceneName}
                        </h2>
                    {onOpenEditor && (
                        <button
                            onClick={onOpenEditor}
                            style={{
                                padding: '5px 12px',
                                border: '1px solid #bdbdbd',
                                borderRadius: 5,
                                backgroundColor: 'white',
                                color: '#555',
                                cursor: 'pointer',
                                fontSize: 12,
                                flexShrink: 0,
                            }}
                        >
                            In Editor öffnen
                        </button>
                    )}
                    <button
                        onClick={onSave}
                        disabled={!dirty || saving}
                        style={{
                            padding: '5px 16px',
                            border: 'none',
                            borderRadius: 5,
                            backgroundColor: dirty ? '#1976d2' : '#e0e0e0',
                            color: dirty ? 'white' : '#aaa',
                            cursor: dirty && !saving ? 'pointer' : 'default',
                            fontSize: 12,
                            fontWeight: 700,
                            flexShrink: 0,
                            transition: 'background-color 0.15s',
                        }}
                    >
                        {saving ? 'Speichert…' : 'Speichern'}
                    </button>
                    </div>{/* end inner flex row */}

                    {/* Category + device tags + story membership */}
                    {(category || targets.length > 0 || storyMembership.length > 0) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            {category && (
                                <span style={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    color: '#7986cb',
                                    backgroundColor: '#e8eaf6',
                                    padding: '2px 8px',
                                    borderRadius: 10,
                                    letterSpacing: 0.4,
                                    textTransform: 'uppercase',
                                }}>
                                    {category}
                                </span>
                            )}
                            {targets.map((t, i) => {
                                const id = typeof t === 'string' ? t : null;
                                const obj = id ? referencedObjects[id] : null;
                                // CBox uses fields.name as its "Identifikation"
                                const devName = obj
                                    ? ((obj.fields && obj.fields.name) || obj.title || id)
                                    : (id || 'Gerät');
                                return (
                                    <span key={i} style={{
                                        fontSize: 10,
                                        fontWeight: 600,
                                        color: '#546e7a',
                                        backgroundColor: '#eceff1',
                                        padding: '2px 8px',
                                        borderRadius: 10,
                                        letterSpacing: 0.3,
                                    }}>
                                        📺 {devName}
                                    </span>
                                );
                            })}
                            {storyMembership.map(story => (
                                <span key={story.id} style={{
                                    fontSize: 10,
                                    fontWeight: 600,
                                    color: '#7b1fa2',
                                    backgroundColor: '#f3e5f5',
                                    padding: '2px 8px',
                                    borderRadius: 10,
                                    letterSpacing: 0.3,
                                }}>
                                    📖 {(story.fields && story.fields.name) || story.title || '?'}
                                </span>
                            ))}
                        </div>
                    )}
                </div>{/* end outer header div */}

                {actions.length === 0 && (
                    <div style={{ color: '#bbb', fontSize: 14, padding: '20px 0' }}>
                        Diese Szene enthält noch keine Aktionen.
                    </div>
                )}

                {/* Action list */}
                <DragDropContext onDragEnd={this.onDragEnd}>
                    <Droppable droppableId="scene-actions">
                        {(provided, snapshot) => (
                            <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                style={{
                                    flex: 1,
                                    overflowY: 'auto',
                                    minHeight: 60,
                                    backgroundColor: snapshot.isDraggingOver ? '#f8fbff' : 'transparent',
                                    borderRadius: 6,
                                    transition: 'background-color 0.15s',
                                    paddingRight: 2,
                                }}
                            >
                                {actions.map((action, index) => (
                                    <Draggable
                                        key={'action-' + index}
                                        draggableId={'action-' + index}
                                        index={index}
                                    >
                                        {(provided, snapshot) => (
                                            <ActionRow
                                                action={action}
                                                index={index}
                                                isSelected={selectedActionIndex === index}
                                                provided={provided}
                                                snapshot={snapshot}
                                                referencedObjects={referencedObjects}
                                                onSelect={() => onSelectAction && onSelectAction(index)}
                                                onDelete={() => onDeleteAction && onDeleteAction(index)}
                                            />
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>

                {actions.length > 0 && (
                    <div style={{ marginTop: 10, fontSize: 11, color: '#bbb', flexShrink: 0 }}>
                        {actions.length} Aktion{actions.length !== 1 ? 'en' : ''} · Ziehen zum Umsortieren · Klick zum Bearbeiten
                    </div>
                )}
            </div>
        );
    }
}

export default SceneTimelineEditor;
